import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/apiService';
import { AuthPageLayout, authLinkStyle, authPrimaryButtonStyle, authTextFieldSx } from './auth/AuthPageLayout';
import { formatUnmetPasswordRules } from './account/passwordPolicy';
import { PasswordRequirements, PasswordVisibilityToggle } from './account/PasswordInputs';

const REDIRECT_SECONDS = 5;

/**
 * Copy for the two ways to land here. "welcome" is /set-password, the link in the invitation
 * email an admin's "create user" sends; it posts to the same endpoint (the invite token is a
 * longer-lived reset token).
 */
const COPY = {
  reset: {
    title: 'Reset Password',
    subtitle: 'Please enter your new password below.',
    missingToken: 'This reset link is missing its token. Please request a new one.',
    missingTokenLink: 'Request a new link',
    expired: 'This reset link is invalid or has expired.',
    newLink: 'Request a new link',
    submit: 'Reset Password',
    doneTitle: 'Password Reset',
    done: 'Your password has been reset.',
    genericError: 'An error occurred while resetting your password.',
  },
  welcome: {
    title: 'Welcome to Flock',
    subtitle: "You've been invited to Flock. Choose a password to finish setting up your account.",
    missingToken: 'This invitation link is incomplete. Open the link from your invitation email again, or ask your administrator to resend it.',
    missingTokenLink: 'Request a password reset link',
    expired: 'This invitation link is invalid or has expired. Ask your administrator to resend the invitation, or',
    newLink: 'request a password reset link',
    submit: 'Set Password',
    doneTitle: "You're all set",
    done: 'Your password has been set.',
    genericError: 'An error occurred while setting your password.',
  },
} as const;

export type ResetPasswordVariant = keyof typeof COPY;

export const ResetPasswordPage = ({ variant = 'reset' }: { variant?: ResetPasswordVariant }) => {
  const copy = COPY[variant];
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [submitError, setSubmitError] = useState<'expired' | 'generic' | null>(null);
  const [policyError, setPolicyError] = useState<string>('');
  const [succeeded, setSucceeded] = useState<boolean>(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(REDIRECT_SECONDS);

  // After a successful reset, count down and send the user to the login page.
  useEffect(() => {
    if (!succeeded) return;
    if (secondsLeft <= 0) {
      navigate('/', { replace: true });
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [succeeded, secondsLeft, navigate]);

  const validatePasswords = (): string => {
    if (!password || !confirmPassword) return 'Both fields are required';
    if (password !== confirmPassword) return 'Passwords do not match';
    return '';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    // "required"/"do not match" go under Confirm; policy problems (ours or the API's) under New Password.
    const passwordError = validatePasswords();
    setError(passwordError);
    // The checklist under the field already lists what's missing; just point at it.
    const policyMessage =
      passwordError || !formatUnmetPasswordRules(password) ? '' : "Password doesn't meet the requirements below";
    setPolicyError(policyMessage);

    if (passwordError || policyMessage) return;

    setIsLoading(true);
    setSubmitError(null);
    try {
      // skipAuth: a 401 here means a bad/expired reset token, not an expired session to refresh.
      await api.post('/api/auth/reset-password', { token, password }, { skipAuth: true });
      setSucceeded(true);
    } catch (error: any) {
      console.error('Reset password failed:', error);
      // 400 = the API's password policy rejected it (source of truth if the client rules drift).
      if (error?.status === 400 && typeof error?.data?.password === 'string') {
        setPolicyError(error.data.password);
      } else {
        setSubmitError(error?.status === 401 ? 'expired' : 'generic');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthPageLayout title={copy.title}>
        <Alert severity="error" sx={{ width: '100%', mt: 1 }}>
          {copy.missingToken}
        </Alert>
        <Box textAlign="center" mt={2}>
          <RouterLink to="/forgot-password" style={authLinkStyle}>{copy.missingTokenLink}</RouterLink>
        </Box>
      </AuthPageLayout>
    );
  }

  if (succeeded) {
    return (
      <AuthPageLayout title={copy.doneTitle}>
        <Alert severity="success" sx={{ width: '100%', mt: 1 }}>
          {copy.done} Redirecting to login in {secondsLeft}s…
        </Alert>
        <Button
          variant="contained"
          fullWidth
          style={authPrimaryButtonStyle}
          onClick={() => navigate('/', { replace: true })}
        >
          Go to Login
        </Button>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout title={copy.title} subtitle={copy.subtitle}>
      {isLoading ? (
        <CircularProgress sx={{ color: 'white', mt: 2 }} />
      ) : (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <TextField
            autoFocus
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            fullWidth
            margin="normal"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!error || !!policyError}
            helperText={policyError}
            sx={authTextFieldSx}
            InputProps={{
              endAdornment: (
                <PasswordVisibilityToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} label="New password" />
              ),
            }}
          />
          <PasswordRequirements password={password} />
          <TextField
            label="Confirm New Password"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            fullWidth
            margin="normal"
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!error}
            helperText={error}
            sx={authTextFieldSx}
            InputProps={{
              endAdornment: (
                <PasswordVisibilityToggle visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)} label="Confirm new password" />
              ),
            }}
          />
          {submitError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {submitError === 'expired' ? (
                <>
                  {copy.expired}{' '}
                  <RouterLink to="/forgot-password">{copy.newLink}</RouterLink>
                </>
              ) : (
                copy.genericError
              )}
            </Alert>
          )}
          <Button type="submit" variant="contained" fullWidth style={authPrimaryButtonStyle}>
            {copy.submit}
          </Button>
          <Box textAlign="center" mt={2}>
            <RouterLink to="/" style={authLinkStyle}>Back to Login</RouterLink>
          </Box>
        </form>
      )}
    </AuthPageLayout>
  );
};

/** /set-password: the invitation link from the welcome email. */
export const SetPasswordPage = () => <ResetPasswordPage variant="welcome" />;
